import type { ProductDefinition, QuestionOption, WizardAnswers, WizardConfig } from "@/types";
import { asList, hasProduct } from "@/engine/answers";
import { isQuestionVisible } from "@/engine/visibility";

export const QUESTION_ORDER = [
  "products",
  "rw_license",
  "rw_version",
  "mv_version",
  "mv_assist_platforms",
  "tasks_os",
  "tasks_intune",
  "offline",
  "network_restricted",
  "use_on_webpages",
  "login_method",
  "mv_win_restrict_online_services",
  "tasks_start_menu",
  "tasks_browser",
] as const;

export function productsForLocale(
  config: WizardConfig,
  locale: string,
): ProductDefinition[] {
  return config.products.filter((product) => {
    if (product.visibleLocales && !product.visibleLocales.includes(locale)) {
      return false;
    }
    if (product.hiddenLocales?.includes(locale)) {
      return false;
    }
    return true;
  });
}

export function defaultProducts(config: WizardConfig, locale: string): string[] {
  return config.defaultProducts[locale] ?? config.defaultProducts.default ?? [];
}

export function licenseQuestionKey(answers: WizardAnswers): string {
  const products = asList(answers.products);
  const hasRwFamily = products.includes("rw") || products.includes("claroread");
  const hasMv = products.includes("mindview");
  if (products.length === 1 && products.includes("rw")) return "questionRwSingle";
  if (hasRwFamily && hasMv) return "questionBoth";
  if (hasMv) return "questionMv";
  return "questionRw";
}

export function loginQuestionKey(answers: WizardAnswers): string {
  return hasProduct(answers, "mindview") && !hasProduct(answers, "rw")
    ? "questionMv"
    : "questionDefault";
}

export function licenseOptions(answers: WizardAnswers): QuestionOption[] {
  const options: QuestionOption[] = [
    {
      value: "group",
      labelKey: "wizard.steps.rw_license.options.group.label",
      tooltipRwKey: "wizard.steps.rw_license.options.group.tooltipRw",
      tooltipMvKey: "wizard.steps.rw_license.options.group.tooltipMv",
    },
    {
      value: "enterprise",
      labelKey: "wizard.steps.rw_license.options.enterprise.label",
    },
  ];
  const products = asList(answers.products);
  if (products.length === 1 && products.includes("rw")) {
    options.push({
      value: "single",
      labelKey: "wizard.steps.rw_license.options.single.label",
      tooltipRwKey: "wizard.steps.rw_license.options.single.tooltip",
    });
  }
  return options;
}

export function rwVersionOptions(answers: WizardAnswers): QuestionOption[] {
  const options: QuestionOption[] = [];
  if (answers.rw_license !== "single") {
    options.push({
      value: "android",
      labelKey: "wizard.steps.rw_version.options.android",
      icon: "android",
    });
    options.push({
      value: "chrome_edge",
      labelKey: "wizard.steps.rw_version.options.chrome_edge",
      icons: ["chrome", "edge"],
    });
    options.push({
      value: "ios",
      labelKey: "wizard.steps.rw_version.options.ios",
      icon: "ios",
    });
  }
  options.push(
    { value: "mac", labelKey: "wizard.steps.rw_version.options.mac", icon: "macos" },
    { value: "win", labelKey: "wizard.steps.rw_version.options.win", icon: "windows" },
  );
  return options;
}

export function visibleQuestionIds(answers: WizardAnswers): string[] {
  return QUESTION_ORDER.filter((id) => isQuestionVisible(id, answers));
}

export function nextVisibleIndex(
  currentId: string | undefined,
  answers: WizardAnswers,
): string | "review" {
  const visible = visibleQuestionIds(answers);
  if (!currentId) return visible[0] ?? "review";
  const index = visible.indexOf(currentId);
  return visible[index + 1] ?? "review";
}

export function previousVisibleIndex(
  currentId: string | undefined,
  answers: WizardAnswers,
): string | "home" {
  const visible = visibleQuestionIds(answers);
  if (!currentId) return "home";
  const index = visible.indexOf(currentId);
  return index > 0 ? visible[index - 1] : "home";
}

export function networkHintKey(answers: WizardAnswers): string | undefined {
  const rwVersions = asList(answers.rw_version);
  const rwNeedsNetwork =
    hasProduct(answers, "rw") &&
    (rwVersions.includes("chrome_edge") ||
      rwVersions.includes("ios") ||
      ((rwVersions.includes("win") || rwVersions.includes("mac")) &&
        answers.offline !== "yes"));
  const tasksNeedsNetwork = hasProduct(answers, "tasks") && asList(answers.tasks_os).length > 0;
  if ([rwNeedsNetwork, hasProduct(answers, "mindview"), tasksNeedsNetwork].filter(Boolean).length > 1) {
    return "wizard.steps.network_restricted.hintBoth";
  }
  return undefined;
}
